import type { ListingPerformanceStats } from '@/api/listingPerformanceStats';
import { getCalendarDaysSince } from '@/helpers/dateUtils';
import {
  calculateRenewablePaymentDueDate,
  getStartOfDayInZurich,
} from '@/helpers/renewablePaymentDueDate';
import {
  BILLING_EVENT_ACTION,
  BUNDLE_PAGE_EVENT_ACTION,
  DOCUMENT_UPLOAD_MODAL_EVENT_LABEL,
  EVENT,
  EVENT_CATEGORY,
  GTMPageType,
  MY_ADS_EVENT_ACTION,
  PaymentTypeToGTMExtraInformation,
  TENANT_PROOF_EVENT_ACTION,
  trackEvent,
  type PageTrackingParameters,
} from '@/services/tracking/GtmTrackingService';
import { normalizeStringOrUndefined } from '@/services/tracking/trackingUtils';
import { BundleTier, DeactivationScheduleTargetTerm, PaymentType, type Insertion } from '@/types';

export function trackMyAdsButtonClick(eventAction: string, eventLabel: string) {
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: eventAction,
    h_event_label_008: eventLabel,
  };

  trackEvent(pageParams);
}

export function trackOpenContactSeekersUpgrade({
  objectReference,
}: {
  objectReference: string;
}): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.OPEN_CONTACT_SEEKERS_UPGRADE,
    h_event_label_008: objectReference,
  };

  trackEvent(pageParams);
}

export function trackCheckoutContactSeekersUpgrade({
  objectReference,
}: {
  objectReference: string;
}): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.CHECKOUT_CONTACT_SEEKERS_UPGRADE,
    h_event_label_008: objectReference,
  };

  trackEvent(pageParams);
}

const UPGRADE_BUNDLE_FEATURE_GROUP = 'listing_tl_booking';
const MY_ADS_UPGRADE_TRANSACTION_TYPE = 'myads_top_listing';
type MY_ADS_UPGRADE_PURCHASE_LIST_ITEMS = 'top_listing' | 'premium_listing';

export const UPGRADE_LISTING_CONVERSION_SOURCE = 'upgrade_listing';
export const VIEWING_APPOINTMENT_UPGRADE_CONVERSION_SOURCE = 'viewing_appointment';

function formatExtraInformation(
  extraInfo: Record<string, string | number | undefined>,
): string | undefined {
  const parts: string[] = [];

  for (const key in extraInfo) {
    const value = extraInfo[key];

    if (value !== undefined && value !== '') {
      parts.push(`${key}=${value}`);
    }
  }

  return parts.length > 0 ? parts.join('&') : undefined;
}

export function trackOpenViewingAppointment({
  insertion,
  performanceStats,
}: {
  insertion: Insertion;
  performanceStats?: ListingPerformanceStats;
}): void {
  const extraInfo = {
    current_bundle: insertion.selectedBundle?.tier?.toLowerCase(),
    views: performanceStats?.totalViews,
    listing_age_days: insertion.firstPublishedDate
      ? getCalendarDaysSince(insertion.firstPublishedDate)
      : undefined,
    leads: performanceStats?.totalLeads,
  };

  const extraInformation = formatExtraInformation(extraInfo);

  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.OPEN_VIEWING_APPOINTMENT,
    h_event_label_008: insertion.objectReference,
    h_listing_advertisement_id_120: insertion.listing?.id ?? insertion.objectReference,
    h_extra_information_144: extraInformation,
  };

  trackEvent(pageParams);
}

export function trackCheckoutViewingAppointment({
  insertion,
  performanceStats,
}: {
  insertion: Insertion;
  performanceStats?: ListingPerformanceStats;
}): void {
  const extraInfo = {
    current_bundle: insertion.selectedBundle?.tier?.toLowerCase(),
    views: performanceStats?.totalViews,
    listing_age_days: insertion.firstPublishedDate
      ? getCalendarDaysSince(insertion.firstPublishedDate)
      : undefined,
    leads: performanceStats?.totalLeads,
  };

  const extraInformation = formatExtraInformation(extraInfo);

  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.CHECKOUT_VIEWING_APPOINTMENT,
    h_event_label_008: insertion.objectReference,
    h_listing_advertisement_id_120: insertion.listing?.id ?? insertion.objectReference,
    h_extra_information_144: extraInformation,
  };

  trackEvent(pageParams);
}

export function trackUpgradeBundleClick(objectReference: string, listingId: string): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_feature_group_001: UPGRADE_BUNDLE_FEATURE_GROUP,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.UPGRADE_BUNDLE,
    h_event_label_008: objectReference,
    h_listing_advertisement_id_120: listingId,
  };

  trackEvent(pageParams);
}

export function trackUpgradeBundleContinue({
  listingId,
  selectedBundleId,
}: {
  listingId: string;
  selectedBundleId?: string;
}): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_feature_group_001: UPGRADE_BUNDLE_FEATURE_GROUP,
    h_event_action_007: MY_ADS_EVENT_ACTION.CONTINUE_TO_REVIEW_CONFIRM,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_label_008: 'in_modal',
    h_product_matching_id_038: selectedBundleId,
    h_listing_advertisement_id_120: listingId,
  };

  trackEvent(pageParams);
}

export function trackUpgradeBundleConfirm({
  listingId,
  selectedBundleId,
  transactionTotal,
  conversionSource = UPGRADE_LISTING_CONVERSION_SOURCE,
  purchasedBundle,
}: {
  listingId: string;
  selectedBundleId?: string;
  transactionTotal: number;
  conversionSource?: string;
  purchasedBundle?: BundleTier.TOP | BundleTier.PREMIUM;
}): void {
  let purchasedListItem: MY_ADS_UPGRADE_PURCHASE_LIST_ITEMS | undefined;

  switch (purchasedBundle) {
    case BundleTier.TOP:
      purchasedListItem = 'top_listing';
      break;
    case BundleTier.PREMIUM:
      purchasedListItem = 'premium_listing';
      break;
  }

  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_feature_group_001: UPGRADE_BUNDLE_FEATURE_GROUP,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.BOOK_TL,
    h_event_label_008: 'in_modal',
    h_product_matching_id_038: selectedBundleId,
    h_listing_advertisement_id_120: listingId,
    h_transaction_total_017: transactionTotal,
    h_conversion_source_061: conversionSource,
    h_transaction_type_063: MY_ADS_UPGRADE_TRANSACTION_TYPE,
    h_purchase_list_items_all_173: purchasedListItem,
  };

  trackEvent(pageParams);
}

export function trackSelectDeactivationScheduleDate(
  objectReference: string,
  targetTerm: DeactivationScheduleTargetTerm,
): void {
  const extraInformation =
    targetTerm === DeactivationScheduleTargetTerm.CURRENT
      ? 'current_billing_period'
      : 'next_billing_period';

  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.SELECT_DEACTIVATE_AD_DATE,
    h_event_label_008: objectReference,
    h_extra_information_144: extraInformation as any,
  };

  trackEvent(pageParams);
}

export function trackConfirmScheduleDeactivation(
  objectReference: string,
  targetTerm: DeactivationScheduleTargetTerm,
): void {
  const extraInformation =
    targetTerm === DeactivationScheduleTargetTerm.CURRENT
      ? 'current_billing_period'
      : 'next_billing_period';

  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.CONFIRM_SCHEDULE_DEACTIVATE_AD,
    h_event_label_008: objectReference,
    h_extra_information_144: extraInformation as any,
  };

  trackEvent(pageParams);
}

function inferDeactivationScheduleTargetTerm(insertion: Insertion): DeactivationScheduleTargetTerm {
  const scheduledDate = insertion.deactivationSchedule?.scheduledDate;
  if (!scheduledDate) {
    return DeactivationScheduleTargetTerm.CURRENT;
  }

  const currentTermEndDate = calculateRenewablePaymentDueDate(insertion, new Date());
  if (!currentTermEndDate) {
    return DeactivationScheduleTargetTerm.CURRENT;
  }

  const scheduled = getStartOfDayInZurich(new Date(scheduledDate)).getTime();
  const current = getStartOfDayInZurich(currentTermEndDate).getTime();

  if (scheduled === current) {
    return DeactivationScheduleTargetTerm.CURRENT;
  }

  const nextTermEndDate = calculateRenewablePaymentDueDate(insertion, currentTermEndDate);
  if (nextTermEndDate) {
    const next = getStartOfDayInZurich(nextTermEndDate).getTime();
    if (scheduled === next) {
      return DeactivationScheduleTargetTerm.NEXT;
    }
  }

  return scheduled > current
    ? DeactivationScheduleTargetTerm.NEXT
    : DeactivationScheduleTargetTerm.CURRENT;
}

function targetTermToExtraInformation(targetTerm: DeactivationScheduleTargetTerm): string {
  return targetTerm === DeactivationScheduleTargetTerm.CURRENT
    ? 'current_billing_period'
    : 'next_billing_period';
}

export function trackRemoveScheduleDeactivationTry(insertion: Insertion): void {
  const targetTerm = inferDeactivationScheduleTargetTerm(insertion);
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.REMOVE_SCHEDULE_DEACTIVATE_AD_TRY,
    h_event_label_008: insertion.objectReference,
    h_extra_information_144: targetTermToExtraInformation(targetTerm) as any,
  };
  trackEvent(pageParams);
}

export function trackRemoveScheduleDeactivation(insertion: Insertion): void {
  const targetTerm = inferDeactivationScheduleTargetTerm(insertion);
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: MY_ADS_EVENT_ACTION.REMOVE_SCHEDULE_DEACTIVATE_AD,
    h_event_label_008: insertion.objectReference,
    h_extra_information_144: targetTermToExtraInformation(targetTerm) as any,
  };
  trackEvent(pageParams);
}

export function trackPublishPpaListing(paymentType: PaymentType): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.INSERTION_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.INSERTION_INTERACTION,
    h_event_action_007: BILLING_EVENT_ACTION.PUBLISH_PPA_LISTING,
    h_event_label_008: PaymentTypeToGTMExtraInformation[paymentType],
  };
  trackEvent(pageParams);
}

export function trackBundle(bundleTier: BundleTier) {
  const eventParams = {
    event: EVENT.INSERTION_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.INSERTION_INTERACTION,
    h_event_action_007: 'get_bundle_info',
    h_event_label_008: bundleTier,
  } as PageTrackingParameters;
  trackEvent(eventParams);
}

export enum AiSuggestionTrackingAction {
  GENERATE = 'generate_ai_description',
  PREFILL = 'prefill_ai_description',
  REGENERATE = 'regenerate_ai_description',
}

export enum InvoicesDownloadTrackingAction {
  OPEN = 'open_invoice_history',
  DOWNLOAD = 'download_invoice',
}

export function trackAiGenerate({
  action,
  listingId,
}: {
  action: AiSuggestionTrackingAction;
  listingId?: string;
}): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: action,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
  };

  trackEvent(pageParams);
}

export function trackInvoicesDownload({
  action,
  params,
}: {
  action: InvoicesDownloadTrackingAction;
  params?: PageTrackingParameters;
}) {
  const pageParams: PageTrackingParameters = {
    event: EVENT.HUB_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.HUB_INTERACTION,
    h_event_action_007: action,
    ...params,
  };

  trackEvent(pageParams);
}

export function trackHOWLinkToastDisplayed({
  offerType,
}: {
  offerType?: string;
} = {}): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.FEATURE_DISPLAYED,
    h_event_category_006: EVENT.FEATURE_DISPLAYED,
    h_event_action_007: 'display_listing_created_modal',
    h_event_label_008: 'my_real_estate',
    h_listing_offer_type_117: normalizeStringOrUndefined(offerType),
  };
  trackEvent(pageParams);
}

export function trackHOWLinkToastCtaClick({
  offerType,
}: {
  offerType?: string;
} = {}): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.BUTTON_CLICK,
    h_event_action_007: 'go_to_my_real_estate',
    h_event_label_008: 'insertion_confirmation_modal',
    h_listing_offer_type_117: normalizeStringOrUndefined(offerType),
  };
  trackEvent(pageParams);
}

export function trackInsertionCrosslistingOption({
  isCrosslisted,
}: {
  isCrosslisted: boolean;
}): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.INSERTION_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.INSERTION_INTERACTION,
    h_event_action_007: BUNDLE_PAGE_EVENT_ACTION.SUBMIT_CROSSLISTING_OPTION,
    h_event_label_008: isCrosslisted ? 'yes' : 'no',
  };

  trackEvent(pageParams);
}

export function trackDocumentUploadModalDisplayed(): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.FEATURE_DISPLAYED,
    h_event_action_007: BUNDLE_PAGE_EVENT_ACTION.DISPLAY_DOCUMENT_UPLOAD_MODAL,
    h_event_label_008: DOCUMENT_UPLOAD_MODAL_EVENT_LABEL,
  };

  trackEvent(pageParams);
}

export function trackDocumentUploadModalSwitchToTop(): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.INSERTION_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.INSERTION_INTERACTION,
    h_event_action_007: BUNDLE_PAGE_EVENT_ACTION.SWITCH_PPA_PACKAGE,
    h_event_label_008: DOCUMENT_UPLOAD_MODAL_EVENT_LABEL,
  };

  trackEvent(pageParams);
}

export function trackDocumentUploadModalContinueWithBasic(): void {
  const pageParams: PageTrackingParameters = {
    event: EVENT.INSERTION_INTERACTION,
    h_event_category_006: EVENT_CATEGORY.INSERTION_INTERACTION,
    h_event_action_007: BUNDLE_PAGE_EVENT_ACTION.CONTINUE_WITH_BASIC_PACKAGE,
    h_event_label_008: DOCUMENT_UPLOAD_MODAL_EVENT_LABEL,
  };

  trackEvent(pageParams);
}

export function trackUploadTenantProof(): void {
  trackEvent({
    event: EVENT.INSERTION_INTERACTION,
    h_event_action_007: TENANT_PROOF_EVENT_ACTION.UPLOAD_TENANT_PROOF,
    h_event_label_008: undefined,
  });
}

const MY_ADS_SUGGESTIONS_LIST_NAME = 'my_ads_suggestions';

interface SuggestionListItem {
  id: string;
}

interface MyAdsSuggestionsListParams {
  allSuggestions: SuggestionListItem[];
  visibleSuggestions: SuggestionListItem[];
}

function joinSuggestionIds(suggestions: SuggestionListItem[]): string | undefined {
  const ids = suggestions.map((suggestion) => suggestion.id).filter((id) => id.length > 0);
  return ids.length > 0 ? ids.join(',') : undefined;
}

function myAdsSuggestionsListParams({
  allSuggestions,
  visibleSuggestions,
}: MyAdsSuggestionsListParams): PageTrackingParameters {
  const allIds = joinSuggestionIds(allSuggestions);
  const visibleIds = joinSuggestionIds(visibleSuggestions);

  return {
    h_event_action_007: GTMPageType.MY_ADS_PAGE,
    h_list_name_086: MY_ADS_SUGGESTIONS_LIST_NAME,
    h_list_items_total_284: allSuggestions.length,
    h_list_items_loaded_285: visibleSuggestions.length,
    ...(allIds ? { h_list_items_all_087: allIds } : {}),
    ...(visibleIds ? { h_list_items_loaded_088: visibleIds } : {}),
  };
}

export function trackMyAdsSuggestionsDisplayed(params: MyAdsSuggestionsListParams): void {
  if (params.visibleSuggestions.length === 0) {
    return;
  }

  const eventLabel = `${params.visibleSuggestions.length}_${params.allSuggestions.length}`;

  trackEvent({
    event: EVENT.LIST_LOAD,
    ...myAdsSuggestionsListParams(params),
    h_event_label_008: eventLabel,
  });
}

export function trackMyAdsSuggestionsCtaClick({
  clickedSuggestion,
  ...params
}: MyAdsSuggestionsListParams & { clickedSuggestion: SuggestionListItem }): void {
  const clickedId = clickedSuggestion.id.length > 0 ? clickedSuggestion.id : undefined;
  const position = params.allSuggestions.findIndex(
    (suggestion) => suggestion.id === clickedSuggestion.id && clickedId !== undefined,
  );

  trackEvent({
    event: EVENT.LIST_CLICKTHROUGH,
    ...myAdsSuggestionsListParams(params),
    ...(clickedId
      ? {
          h_event_label_008: clickedId,
          h_list_item_clicked_id_085: clickedId,
        }
      : {}),
    ...(position >= 0 ? { h_position_on_list_overall_283: position + 1 } : {}),
  });
}
